import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate env variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be defined',
  );
}

// Create a fresh client each time, with debug logging enabled
// Derive the internal project ref (used in localStorage key: sb-<ref>-auth-token)
let projectRef: string | undefined;
try {
  const m = supabaseUrl.match(/^https?:\/\/([a-zA-Z0-9-]+)\.supabase\.co/);
  projectRef = m?.[1];
} catch { /* ignore */ }
export const SUPABASE_PROJECT_REF = projectRef;
export const SUPABASE_AUTH_STORAGE_KEY = projectRef ? `sb-${projectRef}-auth-token` : undefined;

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Explicitly set storage to browser localStorage
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  db: { schema: 'public' },
  global: {
    headers: { 'x-client-info': 'starmap-frontend' },
    fetch: (url, options = {}) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      return fetch(url, { ...options, signal: controller.signal }).finally(() => {
        clearTimeout(timeoutId);
      });
    },
  },
});

// Expose a debug helper for manual inspection in console
if (typeof window !== 'undefined') {
  // @ts-expect-error attach debug helper to window
  window.__dumpSupabaseAuth = () => {
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith('sb-'));
      const entries = keys.map(k => [k, localStorage.getItem(k)] as const);
      // eslint-disable-next-line no-console
      console.log('[SupabaseAuthDump]', { projectRef, expectedKey: SUPABASE_AUTH_STORAGE_KEY, keys, entries });
      return { projectRef, expectedKey: SUPABASE_AUTH_STORAGE_KEY, keys, entries };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Auth dump failed', e);
      return null;
    }
  };
}
